import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Loader2, ArrowRight, Calendar } from 'lucide-react';
import { api } from '../../lib/api';
import { ENTITY_KEYS, ENTITY_SCHEMAS } from './entitySchemas';

const AdminDashboard = () => {
  const [counts, setCounts] = useState({});
  const [bookingsCount, setBookingsCount] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const results = await Promise.all(
          ENTITY_KEYS.map((key) => api.get(`/services/${key}`).then((r) => [key, r.data.length]).catch(() => [key, 0]))
        );
        if (cancelled) return;
        const map = Object.fromEntries(results);
        setCounts(map);

        const bookings = await api.get('/admin/bookings').catch(() => ({ data: [] }));
        if (!cancelled) setBookingsCount(bookings.data.length);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Overview of your travel platform</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/admin/bookings" className="group">
          <Card className="bg-gradient-to-br from-red-500 to-red-700 text-white border-0 hover:shadow-xl transition-all">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <Calendar className="h-6 w-6" />
                <ArrowRight className="h-4 w-4 opacity-60 group-hover:translate-x-1 transition-transform" />
              </div>
              <p className="text-3xl font-bold">{bookingsCount ?? 0}</p>
              <p className="text-sm text-red-100 mt-1">Total Bookings</p>
            </CardContent>
          </Card>
        </Link>

        {ENTITY_KEYS.slice(0, 3).map((key) => (
          <Link to={`/admin/${key}`} key={key} className="group">
            <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 hover:shadow-lg transition-all">
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">{ENTITY_SCHEMAS[key].label}</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{counts[key] ?? 0}</p>
                <p className="text-xs text-red-600 dark:text-red-400 mt-1 group-hover:underline">Manage →</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
        <CardContent className="p-5">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-4">All Catalog</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {ENTITY_KEYS.map((key) => (
              <Link
                key={key}
                to={`/admin/${key}`}
                data-testid={`dashboard-tile-${key}`}
                className="flex items-center justify-between px-3 py-2.5 bg-gray-50 dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 rounded-lg transition-colors text-sm text-gray-700 dark:text-gray-300"
              >
                <span className="truncate">{ENTITY_SCHEMAS[key].label}</span>
                <span className="text-xs font-bold ml-2 px-2 py-0.5 bg-white dark:bg-gray-900 rounded">{counts[key] ?? 0}</span>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
